import React from 'react';
import { Box, Typography } from '@mui/material';
import { DeleteButton } from '@refinedev/mui';
import { useQueryClient } from '@tanstack/react-query';
import { ConnectableElement, useDrag, useDrop } from 'react-dnd';

import { ProjectTemplateTestScenario } from '../../../../../../../../src/types';

interface Props {
  testScenario: ProjectTemplateTestScenario;
  id: string;
  testId: string;
  findTestScenario: (id: string) => {
    projectTemplateTestScenario: ProjectTemplateTestScenario | undefined;
    index: number;
  };
  moveTestScenario: (id: string, atIndex: number) => void;
  mutateTestScenario: () => void;
}
const ScenarioDraggableItem: React.FC<Props> = ({
  testScenario,
  id,
  testId,
  findTestScenario,
  moveTestScenario,
  mutateTestScenario
}) => {
  const queryClient = useQueryClient();
  const originalIndex = findTestScenario(id)?.index;
  const [{ isDragging }, drag] = useDrag(
    () => ({
      type: 'test',
      item: { id, originalIndex },
      collect: monitor => ({
        isDragging: monitor.isDragging()
      }),
      end: (item, monitor) => {
        // Rolls back the test if it was dropped outside an acceptable slot
        const { id: droppedId, originalIndex } = item;
        const didDrop = monitor.didDrop();

        if (!didDrop) {
          moveTestScenario(droppedId, originalIndex);
          return;
        }

        // Hit the backend to update the test order
        mutateTestScenario();
      }
    }),
    [id, moveTestScenario, originalIndex]
  );

  const [, drop] = useDrop(
    () => ({
      accept: 'test',
      hover: ({ id: draggedId }: { id: string; originalIndex: number }) => {
        if (draggedId !== id) {
          const { index: overIndex } = findTestScenario(id);
          moveTestScenario(draggedId, overIndex);
        }
      }
    }),
    [id, moveTestScenario, originalIndex]
  );

  const opacity = isDragging ? 0 : 1;

  return (
    <Box
      component="li"
      ref={(node: ConnectableElement) => drag(drop(node))}
      sx={{
        border: '1px dashed gray',
        padding: '0.5rem 1rem',
        marginBottom: '.5rem',
        backgroundColor: 'background.paper',
        cursor: 'move',
        opacity: opacity
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}
      >
        <Typography>{testScenario.scenario.name}</Typography>
        <Box sx={{ display: 'flex' }}>
          <DeleteButton
            resource="assessment_template_test_scenarios"
            recordItemId={testScenario.id}
            onSuccess={() => {
              // Not awaited, as before: Refine's DeleteButton does not wait on this callback, so the
              // row disappears immediately and the scenario list refetches behind it.
              void queryClient.invalidateQueries({ queryKey: ['testScenarios', testId] });
            }}
            hideText
            confirmTitle={'Do you want to delete Scenario: "' + testScenario.scenario.name + '" from Test?'}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default ScenarioDraggableItem;
